import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Page,
  Card,
  DataTable,
  Badge,
  Button,
  EmptyState,
  Spinner,
  Banner,
  ButtonGroup,
  Modal,
  TextContainer,
  BlockStack,
  InlineStack,
  TextField,
  Select,
  Box,
} from "@shopify/polaris";
import { useNavigate } from "react-router-dom";
import {
  getCampaigns,
  deleteCampaign,
  updateCampaign,
  type Campaign,
} from "../api/campaigns";
import {
  getCampaignAttributionSnapshot,
  type CampaignAttributionSnapshotRow,
} from "../api/analytics";
import { isShopInstallRedirectError } from "../api/admin-api-client";

type StatusFilter = "ALL" | Campaign["status"];

const PLACEMENT_LABELS: Record<string, string> = {
  cart: "Cart page",
  product: "Product page",
  checkout: "Checkout",
  thankyou: "Thank you page",
  homepage: "Homepage",
};

function formatPlacement(placement: string): string {
  return PLACEMENT_LABELS[placement] ?? placement;
}

/** Same logic as Dashboard — order subtotal when one currency, else no fake combined total. */
function formatAttributedSalesLine(
  subtotal: string | null | undefined,
  currency: string | null | undefined
): string {
  if (subtotal == null) return "—";
  const n = Number.parseFloat(subtotal);
  if (Number.isNaN(n)) return subtotal;
  if (currency && /^[A-Z]{3}$/i.test(currency)) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: currency.toUpperCase(),
      }).format(n);
    } catch {
      /* fall through */
    }
  }
  return `${subtotal}${currency ? ` ${currency}` : ""}`;
}

function attributedOrdersCell(
  snap: CampaignAttributionSnapshotRow | undefined
): string {
  if (!snap) return "—";
  return snap.attributedOrders.toLocaleString();
}

function attributedSalesCell(
  snap: CampaignAttributionSnapshotRow | undefined
): string {
  if (!snap) return "—";
  if (snap.attributedSalesMixedCurrency) {
    return "Mixed";
  }
  return formatAttributedSalesLine(
    snap.attributedRevenueSubtotal,
    snap.attributedSalesCurrency
  );
}

function CampaignsListPage() {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [campaignAttributionSnapshot, setCampaignAttributionSnapshot] = useState<
    CampaignAttributionSnapshotRow[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("ALL");

  const [rowActionId, setRowActionId] = useState<string | null>(null);
  const [campaignToEnd, setCampaignToEnd] = useState<{ id: string; name: string } | null>(
    null
  );
  const [campaignToRestore, setCampaignToRestore] = useState<{ id: string; name: string } | null>(
    null
  );
  const [modalActionLoading, setModalActionLoading] = useState(false);

  const loadCampaigns = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [data, attributionRows] = await Promise.all([
        getCampaigns(),
        getCampaignAttributionSnapshot().catch(() => [] as CampaignAttributionSnapshotRow[]),
      ]);
      setCampaigns(data);
      setCampaignAttributionSnapshot(attributionRows);
    } catch (err) {
      if (isShopInstallRedirectError(err)) {
        return;
      }
      setError(err instanceof Error ? err.message : "Failed to load campaigns");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  const filteredCampaigns = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return campaigns.filter((c) => {
      if (statusFilter !== "ALL" && c.status !== statusFilter) {
        return false;
      }
      if (!q) return true;
      return c.name.toLowerCase().includes(q);
    });
  }, [campaigns, searchQuery, statusFilter]);

  const attributionByCampaignId = useMemo(() => {
    const m = new Map<string, CampaignAttributionSnapshotRow>();
    for (const row of campaignAttributionSnapshot) {
      m.set(row.campaignId, row);
    }
    return m;
  }, [campaignAttributionSnapshot]);

  const clearFilters = useCallback(() => {
    setSearchQuery("");
    setStatusFilter("ALL");
  }, []);

  const handleStatusChange = async (id: string, newStatus: Campaign["status"]) => {
    setRowActionId(id);
    setError(null);
    try {
      await updateCampaign(id, { status: newStatus });
      await loadCampaigns();
    } catch (err) {
      if (isShopInstallRedirectError(err)) {
        return;
      }
      setError(err instanceof Error ? err.message : "Failed to update campaign status");
    } finally {
      setRowActionId(null);
    }
  };

  const handleConfirmEndCampaign = async () => {
    if (!campaignToEnd) return;
    setModalActionLoading(true);
    setError(null);
    try {
      await deleteCampaign(campaignToEnd.id);
      setCampaignToEnd(null);
      await loadCampaigns();
    } catch (err) {
      if (isShopInstallRedirectError(err)) {
        return;
      }
      setError(err instanceof Error ? err.message : "Failed to end campaign");
    } finally {
      setModalActionLoading(false);
    }
  };

  const handleConfirmRestoreToDraft = async () => {
    if (!campaignToRestore) return;
    setModalActionLoading(true);
    setError(null);
    try {
      await updateCampaign(campaignToRestore.id, { status: "DRAFT" });
      setCampaignToRestore(null);
      await loadCampaigns();
    } catch (err) {
      if (isShopInstallRedirectError(err)) {
        return;
      }
      setError(err instanceof Error ? err.message : "Failed to move campaign to Draft");
    } finally {
      setModalActionLoading(false);
    }
  };

  const getStatusBadge = (status: Campaign["status"]) => {
    switch (status) {
      case "ACTIVE":
        return <Badge tone="success">Active</Badge>;
      case "PAUSED":
        return <Badge tone="warning">Paused</Badge>;
      case "DRAFT":
        return <Badge>Draft</Badge>;
      case "ENDED":
        return <Badge tone="info">Ended</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const renderRowActions = (campaign: Campaign) => {
    const busy = rowActionId === campaign.id;
    const activeVariantCount =
      campaign.variants?.filter((v) => v.status === "ACTIVE").length ?? 0;

    return (
      <ButtonGroup key={`actions-${campaign.id}`}>
        <Button size="slim" disabled={busy} onClick={() => navigate(`/campaigns/${campaign.id}`)}>
          Edit
        </Button>
        {campaign.status === "ACTIVE" && (
          <Button
            size="slim"
            disabled={busy}
            onClick={() => handleStatusChange(campaign.id, "PAUSED")}
          >
            Pause
          </Button>
        )}
        {campaign.status === "PAUSED" && (
          <Button
            size="slim"
            disabled={busy}
            onClick={() => handleStatusChange(campaign.id, "ACTIVE")}
          >
            Resume
          </Button>
        )}
        {campaign.status === "DRAFT" && (
          <Button
            size="slim"
            disabled={busy}
            variant="primary"
            onClick={() => navigate(`/campaigns/${campaign.id}`)}
          >
            {activeVariantCount === 0 ? "Finish setup" : "Review & activate"}
          </Button>
        )}
        {campaign.status === "ENDED" && (
          <Button
            size="slim"
            disabled={busy}
            onClick={() =>
              setCampaignToRestore({ id: campaign.id, name: campaign.name })
            }
          >
            Move to draft
          </Button>
        )}
        {campaign.status !== "ENDED" && (
          <Button
            size="slim"
            tone="critical"
            disabled={busy}
            onClick={() => setCampaignToEnd({ id: campaign.id, name: campaign.name })}
          >
            End campaign
          </Button>
        )}
      </ButtonGroup>
    );
  };

  const rows = filteredCampaigns.map((campaign) => {
    const variantCount = campaign.variants?.length || 0;
    const sessionCount = campaign._count?.sessions || 0;
    const attributionSnap = attributionByCampaignId.get(campaign.id);

    return [
      campaign.name,
      formatPlacement(campaign.placement),
      getStatusBadge(campaign.status),
      `${variantCount} variant${variantCount !== 1 ? "s" : ""}`,
      `${sessionCount} session${sessionCount !== 1 ? "s" : ""}`,
      attributedOrdersCell(attributionSnap),
      attributedSalesCell(attributionSnap),
      renderRowActions(campaign),
    ];
  });

  const totalCount = campaigns.length;
  const filteredCount = filteredCampaigns.length;
  const subtitle =
    totalCount === 0
      ? undefined
      : statusFilter === "ALL" && !searchQuery.trim()
        ? `${totalCount} campaign${totalCount !== 1 ? "s" : ""}`
        : `Showing ${filteredCount} of ${totalCount} campaign${totalCount !== 1 ? "s" : ""}`;

  const pageMarkup = (
    <>
      {error && (
        <Banner tone="critical" onDismiss={() => setError(null)}>
          {error}
        </Banner>
      )}

      {loading ? (
        <Card>
          <div style={{ padding: "2rem", textAlign: "center" }}>
            <Spinner size="large" />
          </div>
        </Card>
      ) : campaigns.length === 0 ? (
        <Card>
          <EmptyState
            heading="No campaigns yet"
            image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
            action={{ content: "Create campaign", onAction: () => navigate("/campaigns/new") }}
          >
            <p>Create a campaign to run games on your storefront.</p>
          </EmptyState>
        </Card>
      ) : (
        <BlockStack gap="400">
          <Card>
            <div style={{ padding: "1rem" }}>
              <BlockStack gap="400">
                <InlineStack gap="400" wrap align="space-between" blockAlign="end">
                  <Box minWidth="220px" maxWidth="100%">
                    <TextField
                      label="Search campaigns"
                      placeholder="Search by name"
                      value={searchQuery}
                      onChange={setSearchQuery}
                      clearButton
                      autoComplete="off"
                    />
                  </Box>
                  <Box minWidth="200px">
                    <Select
                      label="Status"
                      options={[
                        { label: "All statuses", value: "ALL" },
                        { label: "Active", value: "ACTIVE" },
                        { label: "Paused", value: "PAUSED" },
                        { label: "Draft", value: "DRAFT" },
                        { label: "Ended", value: "ENDED" },
                      ]}
                      value={statusFilter}
                      onChange={(v) => setStatusFilter(v as StatusFilter)}
                    />
                  </Box>
                </InlineStack>
                <TextContainer>
                  <p style={{ margin: 0, fontSize: "0.8125rem", color: "var(--p-color-text-secondary)" }}>
                    Ended campaigns stay in this list for reference. They are not served on the storefront.
                    Use filters to focus on what you are operating day to day.
                  </p>
                </TextContainer>
              </BlockStack>
            </div>
          </Card>

          {filteredCount === 0 ? (
            <Card>
              <EmptyState
                heading="No campaigns match your filters"
                image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                secondaryAction={{
                  content: "Clear filters",
                  onAction: clearFilters,
                }}
              >
                <p>Try a different search or status, or clear filters to see all campaigns.</p>
              </EmptyState>
            </Card>
          ) : (
            <Card>
              <DataTable
                columnContentTypes={[
                  "text",
                  "text",
                  "text",
                  "text",
                  "text",
                  "numeric",
                  "text",
                  "text",
                ]}
                headings={[
                  "Name",
                  "Placement",
                  "Status",
                  "Variants",
                  "Sessions",
                  "Attributed orders",
                  "Attributed sales",
                  "Actions",
                ]}
                rows={rows}
              />
            </Card>
          )}
        </BlockStack>
      )}

      <Modal
        open={campaignToEnd !== null}
        onClose={() => !modalActionLoading && setCampaignToEnd(null)}
        title="End campaign"
        primaryAction={{
          content: "End campaign",
          destructive: true,
          loading: modalActionLoading,
          onAction: handleConfirmEndCampaign,
        }}
        secondaryActions={[
          {
            content: "Cancel",
            disabled: modalActionLoading,
            onAction: () => setCampaignToEnd(null),
          },
        ]}
      >
        <Modal.Section>
          <TextContainer>
            <p>
              <strong>{campaignToEnd?.name ?? ""}</strong> will be set to{" "}
              <strong>Ended</strong>. It will stop being served on your storefront. You can still
              open it from this list or move it back to Draft.
            </p>
          </TextContainer>
        </Modal.Section>
      </Modal>

      <Modal
        open={campaignToRestore !== null}
        onClose={() => !modalActionLoading && setCampaignToRestore(null)}
        title="Move to Draft"
        primaryAction={{
          content: "Move to Draft",
          loading: modalActionLoading,
          onAction: handleConfirmRestoreToDraft,
        }}
        secondaryActions={[
          {
            content: "Cancel",
            disabled: modalActionLoading,
            onAction: () => setCampaignToRestore(null),
          },
        ]}
      >
        <Modal.Section>
          <TextContainer>
            <p>
              <strong>{campaignToRestore?.name ?? ""}</strong> will be set to{" "}
              <strong>Draft</strong>. It will not appear on the storefront until you activate it again
              (subject to variants and validation).
            </p>
          </TextContainer>
        </Modal.Section>
      </Modal>
    </>
  );

  return (
    <Page
      title="Campaigns"
      subtitle={subtitle}
      primaryAction={{ content: "Create campaign", onAction: () => navigate("/campaigns/new") }}
    >
      {pageMarkup}
    </Page>
  );
}

export default CampaignsListPage;
